<script setup lang="ts">
import { computed, ref, toRefs, watch } from 'vue';
import { useRouter } from 'vue-router';
import { Feature, PageName } from '@/api/tracking';
import { DgrDialog } from '@stockmarkteam/donguri-ui';
import AdpDocumentCard from '@/components/common/adp-document-card.vue';
import LoadableButton from '@/components/common/atoms/loadable-button.vue';
import MentionAutocompleteTextarea from '@/components/common/molecules/mention-autocomplete-textarea.vue';
import GroupSelect from '@/components/modals/group-select.vue';
import LatestCommentDisplay from '@/components/modals/latest-comment-display.vue';
import {
  AdpDocument,
  Group,
  isPatent,
  isReport,
  Mention,
  ThemeList,
} from '@/types';
import { documentKey } from '@/utils/adpDocument';
import { useUserInfo } from '@/utils/swr';
import { userSession } from '@/utils/userSession';

const props = defineProps<{
  group?: Group;
  adpDocument: AdpDocument;
  theme?: ThemeList;
  pageName?: PageName;
  feature?: Feature;
  isPostingComment: boolean;
}>();
const { group, isPostingComment } = toRefs(props);

type Submit = { group: Group; content: string; mention: Mention };
const emit = defineEmits<{
  close: [];
  submit: [Submit];
}>();

const router = useRouter();
const { data: user } = useUserInfo();

const initialGroupId = group.value?.id ?? userSession.getLastUsedGroupId();

const selectedGroup = ref<Group | undefined>(undefined);
const setSelectedGroup = (group: Group) => (selectedGroup.value = group);

const commentKey = `comment-${documentKey(props.adpDocument)}`;
// 海外特許とレポートアクション対応で、IDだけだと正しいドキュメントにならない時があるので
// 今までlocal storageに保存されたコメントを移行する
// TODO: リリースして1ヶ月ぐらい経ったら移行をやめる
// 修正日： 2026-06-15
const legacyCommentKey = `comment-${props.adpDocument.id}`;
const legacyComment = localStorage.getItem(legacyCommentKey);
if (legacyComment !== null && localStorage.getItem(commentKey) === null) {
  localStorage.setItem(commentKey, legacyComment);
  localStorage.removeItem(legacyCommentKey);
}
const initialComment = localStorage.getItem(commentKey) ?? undefined;
const commentTextRef = ref<InstanceType<typeof MentionAutocompleteTextarea>>();

const commentContent = ref('');

watch(
  () => commentTextRef.value?.trimmedComment ?? '',
  newContent => {
    if (commentTextRef.value && commentContent.value !== newContent) {
      commentContent.value = newContent;
    }
  },
);

const isPostEnabled = computed(() => {
  return (
    commentContent.value.length > 0 &&
    selectedGroup.value !== undefined &&
    !isPostingComment.value
  );
});

const post = async () => {
  if (isPostEnabled.value) {
    const groupId = selectedGroup.value?.id;
    if (groupId !== undefined && initialGroupId !== groupId) {
      userSession.setLastUsedGroupId(groupId);
    }
    if (!selectedGroup.value) return;
    if (!commentTextRef.value) return;
    const comment = commentTextRef.value.trimmedComment;
    const mention = commentTextRef.value.mention;
    if (comment.length > 0) {
      emit('submit', {
        group: selectedGroup.value,
        content: comment,
        mention: mention,
      });
      localStorage.removeItem(commentKey);
    }
  }
};

const callPostComment = () => {
  if (commentTextRef.value?.trimmedComment !== '') {
    commentTextRef.value?.postComment();
  } else {
    post();
  }
};

const hideDialog = (saveComment = false) => {
  if (isPostingComment.value === false) {
    if (saveComment && commentTextRef.value?.trimmedComment) {
      localStorage.setItem(commentKey, commentTextRef.value?.trimmedComment);
    } else if (
      saveComment &&
      commentTextRef.value?.trimmedComment.length === 0
    ) {
      localStorage.removeItem(commentKey);
    }
    emit('close');
  }
  return true;
};

const moveToAllComments = () => {
  const pageName = {
    article: 'articlePage',
    research_paper: 'researchPaperPage',
    patent: 'patentPage',
    report: 'reportPage',
  }[props.adpDocument.doc_type];
  router.push({
    name: pageName,
    params: {
      id: String(props.adpDocument.id),
      doc_type: props.adpDocument.doc_type,
    },
    query: {
      article_lang: isPatent(props.adpDocument)
        ? props.adpDocument.lang
        : undefined,
      patent_publication_office: isPatent(props.adpDocument)
        ? props.adpDocument.publication_office
        : undefined,
      report_page_number: isReport(props.adpDocument)
        ? props.adpDocument.page_number
        : undefined,
    },
  });
  hideDialog();
};

const initialFocusEl = () => {
  // refが未初期化の場合は早期return
  if (!commentTextRef.value) return null;

  return commentTextRef.value.commentArea as HTMLElement;
};
</script>

<template>
  <DgrDialog
    title="コメント投稿"
    :open="true"
    :close-interceptor-handler="() => hideDialog(true)"
    @cancel="hideDialog"
    :close-on-escape="false"
    :has-footer="false"
    :initial-focus-el="initialFocusEl"
  >
    <template #description>
      <div class="m-body">
        <div class="user-info" v-if="user">
          <img class="user-icon" :src="user.image_url" />
          <div class="username c-title-bold--m">{{ user.user_name }}</div>
        </div>
        <div class="o-group-select">
          <GroupSelect
            @selected="setSelectedGroup"
            options-max-height="240px"
            :initial-group-id="initialGroupId"
          ></GroupSelect>
          <div class="o-group-select__text c-text-regular--m">
            にコメントを投稿
          </div>
        </div>
        <MentionAutocompleteTextarea
          class="o-comment-text-area"
          ref="commentTextRef"
          :placeholder="'コメントを書く'"
          :initial-comment="initialComment"
          :disabled="isPostingComment"
          @post-comment="post"
        ></MentionAutocompleteTextarea>
        <AdpDocumentCard
          :adp-document="adpDocument"
          :theme-id="theme ? theme.id : undefined"
          :show-comment="false"
          :show-user-action="false"
          page-name="comment_creation"
          :feature="feature"
        ></AdpDocumentCard>
        <div class="m-buttons">
          <LoadableButton
            class="m-post-button c-btn c-btn--auto c-btn--AnewsPrimary"
            :class="{ disabled: !isPostEnabled }"
            :disabled="!isPostEnabled"
            @click="callPostComment"
            :is-loading="isPostingComment"
          >
            コメントを投稿
          </LoadableButton>
        </div>
        <div
          class="latest-comment"
          v-if="
            adpDocument.comments.length > 0 && user && pageName !== 'article'
          "
        >
          <div class="spacing-08"></div>
          <LatestCommentDisplay
            :article="adpDocument"
            :user="user"
            :theme="theme"
          >
          </LatestCommentDisplay>
          <div class="spacing-12"></div>
          <div class="comment-link">
            <button
              class="outlined c-outlineBtn c-btn--auto c-btn--small"
              @click="moveToAllComments"
            >
              全てのコメントを見る（返信する）
            </button>
          </div>
        </div>
      </div>
    </template>
  </DgrDialog>
</template>
<style lang="scss" scoped>
.m-body {
  box-sizing: border-box;
  width: 616px;

  .user-info {
    display: flex;
    align-items: center;
    margin-bottom: 16px;

    .user-icon {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      margin-right: 8px;
    }
  }
  .o-group-select {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    .o-group-select__text {
      margin-left: 8px;
    }
  }
  .comment-link {
    display: flex;
    justify-content: flex-end;
    button {
      border: solid 1px $color-green600;
      color: $color-green600;
      background-color: #fff;
    }
    button:hover {
      background-color: $color-green100;
    }
  }
  .o-comment-text-area {
    margin-bottom: 8px;
  }

  .o-error {
    margin-top: 8px;
  }
}

.m-buttons {
  margin-top: 8px;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;

  button {
    margin-bottom: 0;
  }
}
</style>
